import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { IInternetTestDetails, IThunkAPI } from '@/store/types';
import { agentAdminApi } from '@/api/admin/agents';
import type {
  IAgentSearchState,
  IAgentSearch,
  IAgentAccountAndPersonalDetails,
  IAgentGigsDetails,
  IAgentApplicationHistoryDetails,
  IAgentAccountDetails,
  IAgentPersonalDetails,
  IAgentCredentialDetails,
  IAdminAgentSpeedTest,
  IAdminAgentHardwareTest,
  IAgentSupplierDetails,
  ICredentialDetails,
  ISWSReasonDetails,
  ISWSVersionDetails,
  ISWSOrderDetails,
  IAgentGigsPhase,
  ISWSOrderStatus,
  IResetPasswordRequest,
  IESignDocumentsRequest,
  IESignMSADocumentsResponse,
  IESignSOWDocumentsResponse,
  IESignDocumentConfig,
  IESignTemplateListResponse,
  IESignDocumentResponse,
  IUpdateMSA,
  ISOWCreateForm,
  ISOWUpdateForm,
  IMSAForm,
  IESignDocumentContent,
  IGigCountriesResponse,
  IDuplicateDetailsPayload,
  IDuplicateDetails,
  IFraudGrpOption,
  IESignTemplate,
  IBGCheckData,
  IBackgroundStatusForm,
  IBGCheckOptions,
  IAgentTPCredentialDetails,
} from '../types/types';
import type { RootState } from '@/store';
import { Esign, FraudCheckType, KnownErrors } from '@/store/enums';
import type { IFormSelectGroupOption } from '@/components/common/form/types';
import { IMobileVerificationTypes } from '../types/types';

interface IAgentSearchPayload {
  text: string;
  loginType: string;
  pageNum: number;
  pageSize: number;
}

interface IAgentSearchRecords {
  lxUserList: IAgentSearch[];
  recordCount: number;
}

const initialState: IAgentSearchState = {
  selectedUserId: 0,
  serviceProviderList: { lxUserList: [], recordCount: 0 },
  supplierList: [],
  mobileVerificationTypes: [],
  mobileVerificationTypesByCountry: [],
  serviceProviderAccountAndPersonalDetails: {
    userProfile: {
      userId: 0,
      userName: '',
      password: null,
      canLogin: false,
      firstName: '',
      lastName: '',
      supplierId: 0,
      email: '',
      lxEmail: '',
      fullName: '',
    },
    personalInformation: {
      dateOfBirth: '',
      forwardMailText: '',
      notifyVoice: '',
      notifySms: '',
      ssoProviderName: '',
      photoUrl: '',
      fullName: '',
      timezone_region: '',
      shippingCountry: 0,
      smsVerifiedTimestamp: 0,

      shippingStreet1: '',
      shippingStreet2: '',
      shippingCity: '',
      provinceName: '',
      countryname: '',
      shippingPostal: '',
      workHistory: null,
    },
  },
  serviceProviderGigsDetails: [],
  serviceProviderApplicationHistoryDetails: [],
  credentials: {
    activeCredentials: [],
    expiredCredentials: [],
    tpCredentials: {
      corpId: '',
      tpPosition: '',
      networkId: '',
      taxYear: null,
      tpState: '',
      tpCredentialHistoryRecords: [],
    },
  },
  speedTests: [],
  speedTestDetail: null,
  hardwareTests: [],
  credentialList: [],
  swsOrderReasonList: [],
  swsOrderVersionList: [],
  swsOrderDetails: [],
  serviceProviderGigPhaseList: [],
  swsOrderStatusList: [],
  esignMSADocumentList: null,
  esignSOWDocumentList: null,
  esignVersionsList: [],
  esignDocumentConfig: null,
  esignTemplatesList: {
    MSATemplates: null,
    SOWTemplates: null,
  },
  esignDocumentContent: null,
  esignDocument: null,
  gigCountriesList: [],
  duplicateDetails: {
    hardware: {
      testId: 0,
      testRanDate: '',
      duplicate: '',
      usersWithSameDetails: [],
    },
    internet: {
      testId: 0,
      testRanDate: '',
      duplicate: '',
      usersWithSameDetails: [],
    },
  },
  previousGroupDetails: {
    fraudCheckGroupId: 0,
    fraudCheckGroupDescription: '',
    isFraudulent: 0,
  },
};

export const getServiceProvidersList = createAsyncThunk<IAgentSearchRecords, IAgentSearchPayload, IThunkAPI>(
  'get/serviceprovider/list',
  async ({ text, pageNum = 1, pageSize = 10, loginType }, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceproviderList({ text, pageNum, pageSize }, loginType);
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignMSADocuments = createAsyncThunk<IESignMSADocumentsResponse, IESignDocumentsRequest, IThunkAPI>(
  'get/esign/document/msa/list',
  async ({ documentType, pageNum, pageSize, countryIds, clients }, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignMSADocuments({
        documentType,
        pageNum,
        pageSize,
        countryIds,
        clients,
      });
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignSOWDocuments = createAsyncThunk<IESignSOWDocumentsResponse, IESignDocumentsRequest, IThunkAPI>(
  'get/esign/document/sow/list',
  async ({ documentType, pageNum, pageSize, countryIds, clients }, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignSOWDocuments({
        documentType,
        pageNum,
        pageSize,
        countryIds,
        clients,
      });
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignDocVersions = createAsyncThunk<Array<IESignDocumentResponse>, number, IThunkAPI>(
  'get/esign/document/version/list',
  async (eDocId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignVersionsDocuments(eDocId);
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignDocumentConfig = createAsyncThunk<IESignDocumentConfig, undefined, IThunkAPI>(
  'get/esign/document/config',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignDocumentConfig();
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getGigCountries = createAsyncThunk<Array<IGigCountriesResponse>, number, IThunkAPI>(
  'get/member/gigCountry/',
  async (gigId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getGigCountries(gigId);
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignTemplateList = createAsyncThunk<IESignTemplateListResponse, undefined, IThunkAPI>(
  'get/esign/templates',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignTemplateList();
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getESignDocument = createAsyncThunk<
  IESignDocumentResponse,
  { edocID: number; versionNo: number },
  IThunkAPI
>('get/esign/document', async ({ edocID, versionNo }, thunkAPI) => {
  try {
    const response = await agentAdminApi.getDocument(edocID, versionNo);
    return response?.data || {};
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const GetESignDocumentContent = createAsyncThunk<IESignDocumentContent, string, IThunkAPI>(
  'get/esign/pdf',
  async (templateId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getESignDocumentContent(templateId);
      return response?.data || {};
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const createMSADocument = createAsyncThunk<boolean, IMSAForm, IThunkAPI>(
  'get/esign/msadocument',
  async (formValues, thunkAPI) => {
    try {
      const response = await agentAdminApi.createMSADocument(formValues);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const updateMSADocument = createAsyncThunk<boolean, IUpdateMSA, IThunkAPI>(
  'get/esign/msadocument/update',
  async (formValues, thunkAPI) => {
    try {
      const response = await agentAdminApi.updateMSADocument(formValues);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.reponse.data);
    }
  },
);

export const createSOWDocument = createAsyncThunk<boolean, ISOWCreateForm, IThunkAPI>(
  'get/esign/sowdocument',
  async (formValues, thunkAPI) => {
    try {
      const response = await agentAdminApi.createSOWDocument(formValues);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const updateSOWDocument = createAsyncThunk<boolean, ISOWUpdateForm, IThunkAPI>(
  'get/esign/sowdocument/update',
  async (formValues, thunkAPI) => {
    try {
      const response = await agentAdminApi.updateSOWDocument(formValues);
      return response?.data || false;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.reponse.data);
    }
  },
);

export const getServiceProvidersDetails = createAsyncThunk<IAgentAccountAndPersonalDetails, number, IThunkAPI>(
  'get/serviceprovider/details',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderDetails(userId);
      if (response.data === KnownErrors.ItemNotFound) {
        return initialState.serviceProviderAccountAndPersonalDetails;
      }
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getServiceProviderGigsDetails = createAsyncThunk<Array<IAgentGigsDetails>, number, IThunkAPI>(
  'admin/serviceprovider/gigs',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderGigsDetails(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getServiceProviderCredDetails = createAsyncThunk<
  { response: IAgentCredentialDetails[]; currentCredential: number },
  { userId: number; currentCredential: number },
  IThunkAPI
>('admin/serviceprovider/creds', async ({ userId, currentCredential }, thunkAPI) => {
  try {
    const response = await agentAdminApi.getServiceProviderCredDetails(userId, currentCredential);
    return { response: response.data, currentCredential };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getServiceProviderTPCredDetails = createAsyncThunk<
  { response: IAgentTPCredentialDetails },
  { userId: number },
  IThunkAPI
>('admin/serviceprovider/tpcredentials', async ({ userId }, thunkAPI) => {
  try {
    const response = await agentAdminApi.getServiceProviderTPCredDetails(userId);
    return { response: response.data };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getServiceProviderApplicationHistoryDetails = createAsyncThunk<
  Array<IAgentApplicationHistoryDetails>,
  number,
  IThunkAPI
>('admin/serviceprovider/ApplicationHistory', async (userId, thunkAPI) => {
  try {
    const response = await agentAdminApi.getServiceProviderApplicationHistoryDetails(userId);
    return response.data;
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getServiceProviderSpeedTest = createAsyncThunk<Array<IAdminAgentSpeedTest>, number, IThunkAPI>(
  'admin/agent/serviceprovider/speedtest',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderSpeedTest(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSpeedTestDetail = createAsyncThunk<IInternetTestDetails, number, IThunkAPI>(
  'admin/agent/serviceprovider/speedtest/detail',
  async (testId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getSpeedTestDetail(testId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getServiceProviderHardwareTest = createAsyncThunk<Array<IAdminAgentHardwareTest>, number, IThunkAPI>(
  'admin/agent/serviceprovider/hardwaretest',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderHardwareTest(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getServiceProviderSupplierList = createAsyncThunk<Array<IAgentSupplierDetails>, undefined, IThunkAPI>(
  'admin/serviceprovider/supplier/list',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderSupplierList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getMobileVerificationTypes = createAsyncThunk<Array<IMobileVerificationTypes>, undefined, IThunkAPI>(
  'admin/mobileverification/types',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getMobileVerificationTypes();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getMobileVerificationTypesByCountry = createAsyncThunk<number[], number, IThunkAPI>(
  'admin/mobileverification/types/country',
  async (countryId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getMobileVerificationTypesByCountry(countryId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getCredentialList = createAsyncThunk<Array<ICredentialDetails>, undefined, IThunkAPI>(
  'admin/serviceprovider/credentials/list',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getCredentialList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSWSOrderReasonList = createAsyncThunk<Array<ISWSReasonDetails>, undefined, IThunkAPI>(
  'admin/serviceprovider/swsorder/reason',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getSWSReasonList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSWSOrderVersionList = createAsyncThunk<Array<ISWSVersionDetails>, undefined, IThunkAPI>(
  'admin/serviceprovider/swsorder/version',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getSWSVersionList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSWSOrderStatusList = createAsyncThunk<Array<ISWSOrderStatus>, undefined, IThunkAPI>(
  'admin/serviceprovider/swsorder/status',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getSWSStatusList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getSWSOrderDetails = createAsyncThunk<Array<ISWSOrderDetails>, number, IThunkAPI>(
  'admin/serviceprovider/swsorder/list',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getSWSOrderDetails(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getServiceProviderGigPhaseList = createAsyncThunk<Array<IAgentGigsPhase>, undefined, IThunkAPI>(
  'admin/serviceprovider/gigPhase',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getServiceProviderGigPhaseList();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getResetPasswordLink = createAsyncThunk<Array<IResetPasswordRequest>, number, IThunkAPI>(
  'pub/userProfile/reset/generate',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getResetPasswordLink(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getDuplicateDetailsList = createAsyncThunk<
  { data: IDuplicateDetails; fraudCheckTypeId: number },
  IDuplicateDetailsPayload,
  IThunkAPI
>('member/user/fraud-check', async ({ userId, fraudCheckTypeId }, thunkAPI) => {
  try {
    const response = await agentAdminApi.getDuplicateDetailsList(userId, fraudCheckTypeId);
    return { data: response.data, fraudCheckTypeId };
  } catch (e) {
    return thunkAPI.rejectWithValue(e.response.data);
  }
});

export const getPreviousGroupDetails = createAsyncThunk<IFraudGrpOption, number, IThunkAPI>(
  'member/user/getfraudDetails',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getFraudCheckGroupUser(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getBackgroundCheckData = createAsyncThunk<IBGCheckData, number, IThunkAPI>(
  'member/user/getBGCheckData',
  async (userId, thunkAPI) => {
    try {
      const response = await agentAdminApi.getBGCheckUserData(userId);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const saveBackgroundCheckData = createAsyncThunk<any, IBackgroundStatusForm, IThunkAPI>(
  'member/user/saveBGCheckData',
  async (payload, thunkAPI) => {
    try {
      const response = await agentAdminApi.saveBGCheckUserData(payload);
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const getBackgroundCheckOptions = createAsyncThunk<IBGCheckOptions[], undefined, IThunkAPI>(
  'member/user/getBGCheckOptions',
  async (_, thunkAPI) => {
    try {
      const response = await agentAdminApi.getBGCheckOptions();
      return response.data;
    } catch (e) {
      return thunkAPI.rejectWithValue(e.response.data);
    }
  },
);

export const agentAdminSlice = createSlice({
  name: 'agentsAdmin',
  initialState,
  reducers: {
    updateSelectedUserId(state, { payload }) {
      state.selectedUserId = payload;
    },
    clearSelectedUserId(state) {
      state.selectedUserId = 0;
    },
    updateServiceProviderActiveCredential(state, { payload }) {
      const updatedActiveCreds = state.credentials.activeCredentials.map(activeUser => {
        if (activeUser.agentCredentialId === payload.agentCredentialId) {
          activeUser.userName = payload.userName;
          activeUser.passWord = payload.passWord;
        }
        return activeUser;
      });
      state.credentials.activeCredentials = [...updatedActiveCreds];
    },
    updateSWSOrderDetails(state, { payload }) {
      const updatedSWSDetails = state.swsOrderDetails.map(swsOrder => {
        if (swsOrder.usbOrderId === payload.usbOrderId) {
          swsOrder.tracker = payload.tracker;
          swsOrder.orderStatusText = payload.orderStatusText;
          swsOrder.commentText = payload.commentText;
        }
        return swsOrder;
      });
      state.swsOrderDetails = [...updatedSWSDetails];
    },
    resetPreviousGrpDetails(state) {
      state.previousGroupDetails = {
        fraudCheckGroupId: 0,
        fraudCheckGroupDescription: '',
        isFraudulent: 0,
      };
    },
    clearESignDocument(state) {
      state.esignDocument = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getServiceProvidersList.fulfilled, (state, action) => {
        const { lxUserList = [], recordCount = 0 } = action.payload;
        state.serviceProviderList.lxUserList = lxUserList;
        state.serviceProviderList.recordCount = recordCount;
      })
      .addCase(getESignMSADocuments.fulfilled, (state, action) => {
        state.esignMSADocumentList = action.payload;
      })
      .addCase(getESignSOWDocuments.fulfilled, (state, action) => {
        state.esignSOWDocumentList = action.payload;
      })
      .addCase(getESignDocVersions.fulfilled, (state, action) => {
        state.esignVersionsList = action.payload;
      })
      .addCase(getESignDocumentConfig.fulfilled, (state, action) => {
        state.esignDocumentConfig = action.payload;
      })
      .addCase(getGigCountries.fulfilled, (state, action) => {
        state.gigCountriesList = action.payload;
      })
      .addCase(GetESignDocumentContent.fulfilled, (state, action) => {
        state.esignDocumentContent = action.payload;
      })
      .addCase(getESignTemplateList.fulfilled, (state, action) => {
        const MSATemplates: IESignTemplate[] = [];
        const SOWTemplates: IESignTemplate[] = [];
        action.payload?.eSignTemplates.forEach(template => {
          if (template.templateName.indexOf(Esign.Sow) > -1) {
            SOWTemplates.push(template);
          }
          if (template.templateName.indexOf(Esign.Msa) > -1) {
            MSATemplates.push(template);
          }
        });

        state.esignTemplatesList.MSATemplates = MSATemplates;
        state.esignTemplatesList.SOWTemplates = SOWTemplates;
      })
      .addCase(getESignDocument.fulfilled, (state, action) => {
        state.esignDocument = action.payload;
      })
      .addCase(getServiceProvidersDetails.fulfilled, (state, action) => {
        if (action.payload?.userProfile?.fullName && !action.payload?.personalInformation?.fullName) {
          action.payload.personalInformation.fullName = action.payload.userProfile.fullName;
        }
        state.serviceProviderAccountAndPersonalDetails = action.payload;
      })
      .addCase(getServiceProviderGigsDetails.fulfilled, (state, action) => {
        state.serviceProviderGigsDetails = action.payload;
      })
      .addCase(getServiceProviderApplicationHistoryDetails.fulfilled, (state, action) => {
        state.serviceProviderApplicationHistoryDetails = action.payload;
      })
      .addCase(getServiceProviderSpeedTest.fulfilled, (state, action) => {
        state.speedTests = action.payload;
      })
      .addCase(getSpeedTestDetail.fulfilled, (state, action) => {
        state.speedTestDetail = action.payload;
      })
      .addCase(getServiceProviderHardwareTest.fulfilled, (state, action) => {
        state.hardwareTests = action.payload;
      })
      .addCase(getMobileVerificationTypesByCountry.fulfilled, (state, action) => {
        state.mobileVerificationTypesByCountry = action.payload;
      })
      .addCase(getMobileVerificationTypes.fulfilled, (state, action) => {
        state.mobileVerificationTypes = action.payload;
      })
      .addCase(getServiceProviderTPCredDetails.fulfilled, (state, action) => {
        state.credentials.tpCredentials = action.payload.response;
      })
      .addCase(getServiceProviderTPCredDetails.rejected, state => {
        state.credentials.tpCredentials = {} as IAgentTPCredentialDetails;
      })
      .addCase(getServiceProviderCredDetails.fulfilled, (state, action) => {
        if (action.payload?.currentCredential === 0) {
          state.credentials.expiredCredentials = action.payload.response;
        } else {
          state.credentials.activeCredentials = action.payload.response;
        }
      })
      .addCase(getServiceProviderSupplierList.fulfilled, (state, action) => {
        const supplierList =
          Array.isArray(action.payload) &&
          action.payload?.map(supplier => {
            return {
              value: supplier.supplierId,
              label: supplier.supplierName,
            };
          });

        state.supplierList = supplierList || [];
      })
      .addCase(getCredentialList.fulfilled, (state, action) => {
        const credentialList =
          Array.isArray(action.payload) &&
          action.payload?.map(cred => {
            return {
              value: cred.agentCredentialId,
              label: cred.fullName,
            };
          });

        state.credentialList = credentialList || [];
      })
      .addCase(getSWSOrderReasonList.fulfilled, (state, action) => {
        const swsOrderReasonList =
          Array.isArray(action.payload) &&
          action.payload?.map(reason => {
            return {
              value: reason.orderReasonId,
              label: reason.orderReasonText,
            };
          });

        state.swsOrderReasonList = swsOrderReasonList || [];
      })
      .addCase(getSWSOrderVersionList.fulfilled, (state, action) => {
        const swsOrderVersionList =
          Array.isArray(action.payload) &&
          action.payload?.map(version => {
            return {
              value: version.usbVersionId,
              label: version.usbVersionText,
            };
          });

        state.swsOrderVersionList = swsOrderVersionList || [];
      })
      .addCase(getSWSOrderStatusList.fulfilled, (state, action) => {
        const swsOrderStatusList =
          Array.isArray(action.payload) &&
          action.payload?.map(status => {
            return {
              value: status.orderStatusId,
              label: status.orderStatusText,
            };
          });

        state.swsOrderStatusList = swsOrderStatusList || [];
      })
      .addCase(getSWSOrderDetails.fulfilled, (state, action) => {
        state.swsOrderDetails = action.payload;
      })
      .addCase(getServiceProviderGigPhaseList.fulfilled, (state, action) => {
        state.serviceProviderGigPhaseList = action.payload;
      })
      .addCase(getDuplicateDetailsList.fulfilled, (state, action) => {
        if (action.payload.fraudCheckTypeId === FraudCheckType.DuplicateIPAddress) {
          state.duplicateDetails.internet = action.payload.data;
        } else if (action.payload.fraudCheckTypeId === FraudCheckType.DuplicatePCSignature) {
          state.duplicateDetails.hardware = action.payload.data;
        }
      })
      .addCase(getPreviousGroupDetails.fulfilled, (state, action) => {
        state.previousGroupDetails = action.payload;
      });
  },
});

// actions
export const {
  clearSelectedUserId,
  updateSelectedUserId,
  updateServiceProviderActiveCredential,
  updateSWSOrderDetails,
  resetPreviousGrpDetails,
  clearESignDocument,
} = agentAdminSlice.actions;

// selectors
export const getSelectedUserID = (state: RootState): IAgentSearchState['selectedUserId'] =>
  state.agentsAdmin.selectedUserId;

export const serviceProviderList = (state: RootState): IAgentSearch[] =>
  state.agentsAdmin.serviceProviderList.lxUserList;

export const serviceProviderListCount = (state: RootState): number => state.agentsAdmin.serviceProviderList.recordCount;

export const esignMSADocumentsList = (state: RootState): IESignMSADocumentsResponse | null =>
  state.agentsAdmin.esignMSADocumentList;

export const esignSOWDocumentsList = (state: RootState): IESignSOWDocumentsResponse | null =>
  state.agentsAdmin.esignSOWDocumentList;

export const esignDocVersionsList = (state: RootState): IESignDocumentResponse[] | null =>
  state.agentsAdmin.esignVersionsList;

export const esignDocumentsConfig = (state: RootState): IESignDocumentConfig | null =>
  state.agentsAdmin.esignDocumentConfig;

export const gigCountriesList = (state: RootState): IGigCountriesResponse[] => state.agentsAdmin.gigCountriesList;

export const eSignDocumentContent = (state: RootState): IESignDocumentContent | null =>
  state.agentsAdmin.esignDocumentContent;

export const esignMSATemplateList = (state: RootState): IESignTemplate[] | null =>
  state.agentsAdmin.esignTemplatesList.MSATemplates;

export const esignSOWTemplateList = (state: RootState): IESignTemplate[] | null =>
  state.agentsAdmin.esignTemplatesList.SOWTemplates;

export const esignDocument = (state: RootState): IESignDocumentResponse | null => state.agentsAdmin.esignDocument;

export const mobileVerificationTypesByCountry = (state: RootState): number[] =>
  state.agentsAdmin.mobileVerificationTypesByCountry;

export const serviceProviderUserProfileDetails = (state: RootState): IAgentAccountDetails =>
  state.agentsAdmin.serviceProviderAccountAndPersonalDetails.userProfile;

export const serviceProviderPersonalInformation = (state: RootState): IAgentPersonalDetails =>
  state.agentsAdmin.serviceProviderAccountAndPersonalDetails.personalInformation;

export const gigDetails = (state: RootState): IAgentSearchState['serviceProviderGigsDetails'] =>
  state.agentsAdmin.serviceProviderGigsDetails;

export const applicationHistoryDetails = (
  state: RootState,
): IAgentSearchState['serviceProviderApplicationHistoryDetails'] =>
  state.agentsAdmin.serviceProviderApplicationHistoryDetails;

export const getServiceProviderActiveCredentials = (state: RootState): IAgentCredentialDetails[] =>
  state.agentsAdmin.credentials.activeCredentials;

export const getServiceProviderExpiredCredentials = (state: RootState): IAgentCredentialDetails[] =>
  state.agentsAdmin.credentials.expiredCredentials;

export const getServiceProviderTPCredentials = (state: RootState): IAgentTPCredentialDetails =>
  state.agentsAdmin.credentials.tpCredentials;

export const speedTests = (state: RootState): Array<IAdminAgentSpeedTest> => state.agentsAdmin.speedTests;
export const speedTestDetail = (state: RootState): IInternetTestDetails | null => state.agentsAdmin.speedTestDetail;

export const hardwareTests = (state: RootState): Array<IAdminAgentHardwareTest> => state.agentsAdmin.hardwareTests;

export const getSupplierList = (state: RootState): IFormSelectGroupOption[] => state.agentsAdmin.supplierList;

export const getCredList = (state: RootState): IFormSelectGroupOption[] => state.agentsAdmin.credentialList;

export const getReasonList = (state: RootState): IFormSelectGroupOption[] => state.agentsAdmin.swsOrderReasonList;

export const getVersionList = (state: RootState): IFormSelectGroupOption[] => state.agentsAdmin.swsOrderVersionList;

export const getStatusList = (state: RootState): IFormSelectGroupOption[] => state.agentsAdmin.swsOrderStatusList;

export const getSwsOrderList = (state: RootState): IAgentSearchState['swsOrderDetails'] =>
  state.agentsAdmin.swsOrderDetails;

export const gigPhaseList = (state: RootState): IAgentSearchState['serviceProviderGigPhaseList'] =>
  state.agentsAdmin.serviceProviderGigPhaseList;

export const getDuplicateIPDetails = (state: RootState): IDuplicateDetails =>
  state.agentsAdmin.duplicateDetails.internet;

export const getDuplicateHardwareDetails = (state: RootState): IDuplicateDetails =>
  state.agentsAdmin.duplicateDetails.hardware;

export const getPreviousGrpDetails = (state: RootState): IAgentSearchState['previousGroupDetails'] =>
  state.agentsAdmin.previousGroupDetails;

export default agentAdminSlice.reducer;
